// src/pages/Refund.jsx
import React, { useEffect, useState, useRef } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { FaChevronDown, FaChevronUp, FaCopy } from "react-icons/fa";

const RULES = [
  {
    id: 1,
    title: "Refund Before Development",
    body: "100% refund is available if the project is canceled before development begins.",
  },
  {
    id: 2,
    title: "Cancellation Within 3 Days",
    body: "50% refund is applicable if the project is canceled within the first 3 days of development.",
  },
  {
    id: 3,
    title: "50% Completion Rule",
    body: "No refund is provided once project progress exceeds 50% of the work scope.",
  },
  {
    id: 4,
    title: "Domain & Hosting",
    body: "Domain or hosting purchases are non-refundable under any circumstances as they are third-party services.",
  },
];

export default function Refund() {
  const [open, setOpen] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const neonRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 700, once: true, easing: "ease-out-quart" });
  }, []);

  // small parallax for background neon (desktop only)
  useEffect(() => {
    const el = neonRef.current;
    if (!el) return;
    let raf;
    const move = (e) => {
      const cx = e.touches?.[0]?.clientX ?? e.clientX;
      const cy = e.touches?.[0]?.clientY ?? e.clientY;
      const x = ((cx / window.innerWidth) - 0.5) * 24;
      const y = ((cy / window.innerHeight) - 0.5) * 18;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--nx", `${x}px`);
        el.style.setProperty("--ny", `${y}px`);
      });
    };
    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("touchmove", move, { passive: true });
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const toggle = (id) => setOpen((prev) => (prev === id ? null : id));

  const copyText = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopiedId(null), 1500);
    } catch {
      // fallback: show "copied" briefly even if clipboard fails
      setCopiedId(id);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopiedId(null), 1500);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] relative text-[#0b1220] pt-28 pb-12 px-4 sm:px-6">
      {/* Neon Background */}
      <div
        ref={neonRef}
        className="absolute inset-0 -z-10 pointer-events-none neon-bg"
        aria-hidden="true"
      />

      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <header className="mb-8 text-center" data-aos="fade-down">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-2 leading-tight">
            Refund & Cancellation Policy
          </h1>
          <p className="text-slate-600 text-sm sm:text-base">
            Clear, transparent, customer-friendly refund conditions — simple and fair.
          </p>
        </header>

        {/* Policy list */}
        <div className="space-y-4">
          {RULES.map((r) => {
            const isOpen = open === r.id;

            return (
              <article
                key={r.id}
                className="rounded-2xl bg-white/90 backdrop-blur-[6px] border border-slate-200 shadow-sm overflow-hidden"
                data-aos="fade-up"
                aria-labelledby={`rule-${r.id}-title`}
              >
                <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-5 gap-3">
                  <div className="flex-1 min-w-0">
                    <h3
                      id={`rule-${r.id}-title`}
                      className="text-base sm:text-lg font-semibold truncate"
                    >
                      {r.title}
                    </h3>
                    <div className="text-xs text-slate-500 mt-1">Clause #{r.id}</div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Copy button */}
                    <button
                      onClick={() => copyText(r.id, `${r.title}\n\n${r.body}`)}
                      className="p-2 rounded-md hover:bg-slate-100 transition focus:outline-none focus:ring-2 focus:ring-[#60a5fa]/30"
                      title="Copy clause text"
                      aria-label={`Copy clause ${r.id}`}
                    >
                      <FaCopy className="text-slate-600" />
                    </button>

                    {/* Expand toggle (keyboard friendly) */}
                    <button
                      onClick={() => toggle(r.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggle(r.id);
                        }
                      }}
                      aria-expanded={isOpen}
                      aria-controls={`rule-body-${r.id}`}
                      className={`flex items-center gap-2 p-2 rounded-md transition focus:outline-none focus:ring-2 focus:ring-[#60a5fa]/30 ${
                        isOpen ? "bg-slate-50" : "hover:bg-slate-50"
                      }`}
                      title={isOpen ? "Collapse" : "Read"}
                    >
                      <span className="sr-only">{isOpen ? "Collapse" : "Read"}</span>
                      <span className={`inline-block transform transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`}>
                        {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                      </span>
                    </button>
                  </div>
                </header>

                {/* Body (smooth slide) */}
                <div
                  id={`rule-body-${r.id}`}
                  className={`px-5 pb-5 transition-[max-height,opacity,transform] duration-350 ease-in-out ${
                    isOpen ? "max-h-[360px] opacity-100 translate-y-0" : "max-h-0 opacity-0 -translate-y-2"
                  }`}
                >
                  <p className="text-sm text-slate-700 leading-relaxed">{r.body}</p>

                  {/* Copy Feedback */}
                  {copiedId === r.id && (
                    <div className="inline-block mt-3 px-3 py-1 rounded-full bg-[#60a5fa]/20 text-sm text-[#0b1220]">
                      Copied ✓
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-8">
          <p className="text-sm text-slate-600 mb-4">Have refund-related questions?</p>
          <a
            href="/contact"
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-[#06b6d4] to-[#3b82f6] text-white shadow-md hover:scale-[1.02] transition focus:outline-none focus:ring-4 focus:ring-[#06b6d4]/25"
          >
            Contact Support
          </a>
        </div>
      </div>

      {/* Styles */}
      <style>{`
        /* neon / soft backdrop used as decorative background */
        .neon-bg {
          background:
            radial-gradient(closest-side at 6% 18%, rgba(59,130,246,0.12), transparent 6%),
            radial-gradient(closest-side at 86% 10%, rgba(6,182,212,0.10), transparent 6%),
            radial-gradient(closest-side at 60% 80%, rgba(251,113,133,0.06), transparent 8%),
            linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%);
          position: absolute;
          inset: 0;
          filter: blur(30px) saturate(1.02);
          transform: translate3d(var(--nx, 0px), var(--ny, 0px), 0);
          transition: transform 150ms linear;
        }

        /* prefers-reduced-motion: reduce animations */
        @media (prefers-reduced-motion: reduce) {
          .neon-bg, [data-aos] { transition: none !important; animation: none !important; }
        }

        /* card hover subtle lift on desktop */
        @media (hover: hover) and (pointer: fine) {
          article:hover { transform: translateY(-4px); transition: transform 160ms ease; }
        }

        /* make the expand area smoother for smaller screens */
        @media (max-width: 640px) {
          .min-h-screen { padding-top: 20px; }
          .max-w-3xl { padding-left: 6px; padding-right: 6px; }
        }
      `}</style>
    </div>
  );
}
